import React, { useEffect, useMemo, useState } from 'react';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../../lib/designSystem/HeaderWithActions';
import {
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Container,
  Dialog,
  DialogContent,
  Grid,
  Typography,
} from '@mui/material';
import { Permission, Role } from '..';
import { assignPermissionsToRole } from '../../../../../../lib/api/users/roles';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../../../../lib/functions/utils';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';

type Props = {
  open: boolean;
  onClose: () => void;
  role: Role;
  allPermissions: Permission[];
  onPermissionsUpdated?: () => Promise<void>;
};

const PermissionAssignment: React.FC<Props> = ({
  open,
  onClose,
  role,
  allPermissions,
  onPermissionsUpdated,
}) => {
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<
    Record<string, boolean>
  >({});
  const [selectedPermissionForDetails, setSelectedPermissionForDetails] =
    useState<Permission | null>(null);

  const permissionsByType: Record<string, Permission[]> = useMemo(() => {
    const grouped: Record<string, Permission[]> = {};

    allPermissions.forEach((perm) => {
      if (!grouped[perm.type]) grouped[perm.type] = [];
      grouped[perm.type].push(perm);
    });

    Object.keys(grouped).forEach((type) => {
      grouped[type].sort((a, b) =>
        a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }),
      );
    });

    return grouped;
  }, [allPermissions]);

  const permissionsSortedByType = Object.keys(permissionsByType).sort((a, b) =>
    a.localeCompare(b, 'es', { sensitivity: 'base' }),
  );

  useEffect(() => {
    if (role) {
      const initial: Record<string, boolean> = {};
      role.permissions.forEach((perm) => {
        initial[perm.name] = true;
      });
      setSelectedPermissions(initial);
    }
  }, [role]);

  const togglePermission = (permName: string) => {
    setSelectedPermissions((prev) => ({
      ...prev,
      [permName]: !prev[permName],
    }));
  };

  const toggleAllForType = (type: string) => {
    const perms = permissionsByType[type] || [];
    const allSelected = perms.every((p) => selectedPermissions[p.name]);
    const updated = { ...selectedPermissions };
    perms.forEach((p) => {
      updated[p.name] = !allSelected;
    });
    setSelectedPermissions(updated);
  };

  const isAllSelectedForType = (type: string) =>
    permissionsByType[type]?.every((p) => selectedPermissions[p.name]);

  const getSelectedPermissions = () => {
    return Object.keys(selectedPermissions).filter(
      (perm) => selectedPermissions[perm],
    );
  };

  const getSelectedCountForType = (type: string) => {
    const permissionsOfType = permissionsByType[type] || [];
    return permissionsOfType.filter((p) => selectedPermissions[p.name]).length;
  };

  const getTotalCountForType = (type: string) => {
    return permissionsByType[type]?.length || 0;
  };

  const handleSave = async () => {
    setLoading(true)
    try {
      await assignPermissionsToRole(dispatch, {
        id: role.id,
        permissions: getSelectedPermissions(),
      });
      if (onPermissionsUpdated) onPermissionsUpdated();
      showAlert(dispatch, 'success', 'Permisos guardados correctamente');
    } catch (error) {
      console.error(error);
    } finally {
      onClose();
      setLoading(false)
    }
  };

  const buttons: CustomButtonProps[] = [
    {
      name: 'Atrás',
      action: () => onClose(),
      color: 'secondary',
    },
    {
      name: 'Guardar',
      action: () => handleSave(),
      color: 'primary',
    },
  ];

  if (loading) {
    return <CenteredCircularProgress />
  }

  return (
    <div>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            m: 2,
            height: 'auto',
            maxHeight: '700px',
            minHeight: '600px',
          },
        }}
      >
        <DialogContent sx={{ overflow: 'hidden' }}>
          <HeaderWithActions
            title={`Permisos de rol: ${role?.name || ''}`}
            buttons={buttons}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Container
                sx={{
                  backgroundColor: '#f9f9f9',
                  p: 2,
                  borderRadius: 2,
                  maxHeight: 600,
                  overflow: 'auto',
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Tipos de permisos
                </Typography>
                {permissionsSortedByType.map((type) => (
                  <Card
                    key={type}
                    variant="outlined"
                    sx={{
                      mb: 1,
                      backgroundColor:
                        selectedType === type ? '#EDF0FD' : 'white',
                      borderColor: selectedType === type ? '#0F23DF' : '',
                    }}
                  >
                    <CardActionArea
                      onClick={() => {
                        setSelectedType(type);
                        setSelectedPermissionForDetails(null);
                      }}
                    >
                      <CardContent
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight:
                              selectedType === type ? 'bold' : 'normal',
                            color: selectedType === type ? '#0F23DF' : '',
                          }}
                        >
                          {type}
                        </Typography>
                        <Typography>
                          {getSelectedCountForType(type)} /{' '}
                          {getTotalCountForType(type)}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Container>
            </Grid>
            <Grid item xs={12} md={4}>
              <Container
                sx={{
                  backgroundColor: '#f9f9f9',
                  p: 2,
                  borderRadius: 2,
                  maxHeight: 600,
                  overflow: 'auto',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Permisos
                  </Typography>
                  <Checkbox
                    checked={isAllSelectedForType(selectedType!)}
                    onClick={() => toggleAllForType(selectedType!)}
                  />
                </div>
                {selectedType ? (
                  permissionsByType[selectedType]?.map((perm) => (
                    <Card
                      key={perm.name}
                      variant="outlined"
                      sx={{
                        mb: 1,
                        backgroundColor:
                          selectedPermissionForDetails?.name === perm.name
                            ? '#EDF0FD'
                            : 'white',
                        borderColor:
                          selectedPermissionForDetails?.name === perm.name
                            ? '#0F23DF'
                            : '',
                      }}
                    >
                      <CardActionArea
                        onClick={() => setSelectedPermissionForDetails(perm)}
                      >
                        <CardContent
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            py: 0.5,
                          }}
                        >
                          <Checkbox
                            checked={!!selectedPermissions[perm.name]}
                            onClick={() => togglePermission(perm.name)}
                          />
                          <Typography
                            sx={{
                              fontWeight:
                                selectedPermissionForDetails?.name === perm.name
                                  ? 'bold'
                                  : 'normal',
                              color:
                                selectedPermissionForDetails?.name === perm.name
                                  ? '#0F23DF'
                                  : '',
                            }}
                          >
                            {perm.label}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Selecciona un tipo de permiso
                  </Typography>
                )}
              </Container>
            </Grid>
            <Grid item xs={12} md={4}>
              <Container
                sx={{ backgroundColor: '#f9f9f9', p: 2, borderRadius: 2 }}
              >
                <Typography variant="h6" gutterBottom>
                  Descripción
                </Typography>
                {selectedPermissionForDetails ? (
                  <Typography>
                    {selectedPermissionForDetails.description}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Selecciona un permiso para ver su detalle
                  </Typography>
                )}
              </Container>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PermissionAssignment;
